import React, {FC} from 'react';

import modelAiWrite1 from 'assets/v3/guide/model/ai-write-1.webp';
import modelAiWrite2 from 'assets/v3/guide/model/ai-write-2.webp';
import modelEntry from 'assets/v3/guide/model/entry.webp';
import modelRegen from 'assets/v3/guide/model/regen.webp';
import modelShowClothes1 from 'assets/v3/guide/model/show-clothes-1.webp';
import modelShowClothes2 from 'assets/v3/guide/model/show-clothes-2.webp';
import modelShowCup1 from 'assets/v3/guide/model/show-cup-1.webp';
import modelShowCup2 from 'assets/v3/guide/model/show-cup-2.webp';
import modelShowEarring1 from 'assets/v3/guide/model/show-earring-1.webp';
import modelShowEarring2 from 'assets/v3/guide/model/show-earring-2.webp';
import modelShowLamp1 from 'assets/v3/guide/model/show-lamp-1.webp';
import modelShowLamp2 from 'assets/v3/guide/model/show-lamp-2.webp';
import modelShowNecklace1 from 'assets/v3/guide/model/show-necklace-1.webp';
import modelShowNecklace2 from 'assets/v3/guide/model/show-necklace-2.webp';
import modelShowPruner1 from 'assets/v3/guide/model/show-pruner-1.webp';
import modelShowPruner2 from 'assets/v3/guide/model/show-pruner-2.webp';
import modelUploadFront from 'assets/v3/guide/model/upload-front.webp';
import modelUploadScene from 'assets/v3/guide/model/upload-scene.webp';
import sceneAiWrite1 from 'assets/v3/guide/scene/ai-write-1.webp';
import sceneAiWrite2 from 'assets/v3/guide/scene/ai-write-2.webp';
import sceneEntry from 'assets/v3/guide/scene/entry.webp';
import sceneRegen from 'assets/v3/guide/scene/regen.webp';
import sceneShowEarphone1 from 'assets/v3/guide/scene/show-earphone-1.webp';
import sceneShowEarphone2 from 'assets/v3/guide/scene/show-earphone-2.webp';
import sceneShowFeeder1 from 'assets/v3/guide/scene/show-feeder-1.webp';
import sceneShowFeeder2 from 'assets/v3/guide/scene/show-feeder-2.webp';
import sceneShowNecklace1 from 'assets/v3/guide/scene/show-necklace-1.webp';
import sceneShowNecklace2 from 'assets/v3/guide/scene/show-necklace-2.webp';
import sceneShowOil1 from 'assets/v3/guide/scene/show-oil-1.webp';
import sceneShowOil2 from 'assets/v3/guide/scene/show-oil-2.webp';
import sceneShowPerfume1 from 'assets/v3/guide/scene/show-perfume-1.webp';
import sceneShowPerfume2 from 'assets/v3/guide/scene/show-perfume-2.webp';
import sceneShowPruner1 from 'assets/v3/guide/scene/show-pruner-1.webp';
import sceneShowPruner2 from 'assets/v3/guide/scene/show-pruner-2.webp';
import sceneShowUmbrella1 from 'assets/v3/guide/scene/show-umbrella-1.webp';
import sceneShowUmbrella2 from 'assets/v3/guide/scene/show-umbrella-2.webp';
import sceneUploadSample from 'assets/v3/guide/scene/upload-sample.webp';

/** Русский текст вкладки «Модель/сцена» руководства (структура элементов 1:1 с zh.tsx). 2026-09-17 AI-перевод */
const RuContent: FC = () => {
  return (
    <>
      <h2>Генерация изображений товара со сценой</h2>

      <h3>1. Генерация изображений со сценой</h3>
      <img alt="Вход в генерацию со сценой" decoding="async" loading="lazy" src={sceneEntry} />
      <p>
        Загрузите 1–3 фотографии товара. Полные и разнообразные снимки дают более качественные и точные изображения
        товара со сценой.
      </p>
      <img alt="Пример загрузки товара" decoding="async" loading="lazy" src={sceneUploadSample} />
      <p>
        Если у вас есть конкретные требования к сцене, нажмите «+ Введите текстовое описание (необязательно)» и впишите
        их; если нет — просто доверьтесь AI.
      </p>
      <p>При написании описания учитывайте следующее:</p>
      <ul>
        <li>
          Опишите конкретную сцену, а не что-то расплывчатое вроде «в помещении». Опишите сцену, которую вы
          представляете для товара, например: товар на журнальном столике в гостиной, на переднем плане цветы, рядом
          стакан воды.
        </li>
        <li>
          В изображениях со сценой случайно могут появиться люди, взаимодействующие с товаром. Если люди не нужны,
          добавьте в описание: на изображении не должно быть людей.
        </li>
      </ul>
      <p>
        Выберите модель, соотношение сторон, разрешение и количество изображений, затем нажмите кнопку 【Сгенерировать
        сейчас】 — и всё готово.
      </p>

      <h3>2. Повторная генерация</h3>
      <p>
        Нужно больше изображений со сценой? На той же странице просто нажмите кнопку 【Сгенерировать ещё раз】. Если
        есть конкретные требования к сцене, нажмите «+ Введите текстовое описание».
      </p>
      <img alt="Повторная генерация" decoding="async" loading="lazy" src={sceneRegen} />
      <p>Нет идей? Попробуйте AI-подсказку, затем подправьте промпт и сгенерируйте снова.</p>
      <img alt="AI-подсказка" decoding="async" loading="lazy" src={sceneAiWrite1} />
      <img alt="Правка промпта и повторная генерация" decoding="async" loading="lazy" src={sceneAiWrite2} />

      <h3>3. Примеры</h3>
      <h4>Секатор</h4>
      <figure className="v3-img-pair">
        <img alt="Секатор, сцена 1" decoding="async" loading="lazy" src={sceneShowPruner1} />
        <img alt="Секатор, сцена 2" decoding="async" loading="lazy" src={sceneShowPruner2} />
      </figure>
      <h4>Духи</h4>
      <figure className="v3-img-pair">
        <img alt="Духи, сцена 1" decoding="async" loading="lazy" src={sceneShowPerfume1} />
        <img alt="Духи, сцена 2" decoding="async" loading="lazy" src={sceneShowPerfume2} />
      </figure>
      <h4>Наушники</h4>
      <figure className="v3-img-pair">
        <img alt="Наушники, сцена 1" decoding="async" loading="lazy" src={sceneShowEarphone1} />
        <img alt="Наушники, сцена 2" decoding="async" loading="lazy" src={sceneShowEarphone2} />
      </figure>
      <h4>Зонт</h4>
      <figure className="v3-img-pair">
        <img alt="Зонт, сцена 1" decoding="async" loading="lazy" src={sceneShowUmbrella1} />
        <img alt="Зонт, сцена 2" decoding="async" loading="lazy" src={sceneShowUmbrella2} />
      </figure>
      <h4>Кормушка для питомцев</h4>
      <figure className="v3-img-pair">
        <img alt="Кормушка для питомцев, сцена 1" decoding="async" loading="lazy" src={sceneShowFeeder1} />
        <img alt="Кормушка для питомцев, сцена 2" decoding="async" loading="lazy" src={sceneShowFeeder2} />
      </figure>
      <h4>Колье</h4>
      <figure className="v3-img-pair">
        <img alt="Колье, сцена 1" decoding="async" loading="lazy" src={sceneShowNecklace1} />
        <img alt="Колье, сцена 2" decoding="async" loading="lazy" src={sceneShowNecklace2} />
      </figure>
      <h4>Эфирное масло</h4>
      <figure className="v3-img-pair">
        <img alt="Эфирное масло, сцена 1" decoding="async" loading="lazy" src={sceneShowOil1} />
        <img alt="Эфирное масло, сцена 2" decoding="async" loading="lazy" src={sceneShowOil2} />
      </figure>

      <h2>Генерация изображений товара с моделью</h2>

      <h3>1. Генерация изображений с моделью</h3>
      <img alt="Вход в генерацию с моделью" decoding="async" loading="lazy" src={modelEntry} />
      <p>
        Загрузите 1–3 фотографии товара. Полные и разнообразные снимки дают более качественные и точные изображения с
        моделью. Лучше всего подходят виды товара спереди и сзади, например:
      </p>
      <img alt="Пример загрузки вида спереди" decoding="async" loading="lazy" src={modelUploadFront} />
      <p>Вид спереди</p>
      <img alt="Пример AI-сцены" decoding="async" loading="lazy" src={modelUploadScene} />
      <p>Изображение AI-сцены</p>
      <p>
        Если у вас есть конкретные требования к модели, нажмите «+ Введите текстовое описание (необязательно)» и впишите
        их; если нет — просто доверьтесь AI.
      </p>
      <p>При написании описания укажите, какая модель вам нужна:</p>
      <ul>
        <li>Пол</li>
        <li>Возраст или возрастной диапазон (молодой / средний / пожилой или конкретный возраст)</li>
        <li>Тон кожи (например, азиатский, белый, чёрный, латиноамериканский и т. д.)</li>
        <li>Другое (выражение лица, причёска и т. д.)</li>
      </ul>
      <p>
        Чтобы настроить сцену, в которой находится модель, здесь тоже избегайте расплывчатых формулировок вроде «в
        помещении» — напишите, например: в спальне в американском винтажном стиле.
      </p>
      <p>Пример описания: женщина, 20 лет, белая кожа, кудрявые волосы, в спальне в американском винтажном стиле.</p>
      <p>
        Выберите модель, соотношение сторон, разрешение и количество изображений, затем нажмите кнопку 【Сгенерировать
        сейчас】 — и всё готово.
      </p>

      <h3>2. Повторная генерация</h3>
      <p>
        Нужно больше изображений с моделью? На странице генерации просто нажмите кнопку 【Сгенерировать ещё раз】. Если
        есть конкретные требования к сцене, нажмите «+ Введите текстовое описание (необязательно)».
      </p>
      <img alt="Повторная генерация" decoding="async" loading="lazy" src={modelRegen} />
      <p>Нет идей? Попробуйте AI-подсказку, затем подправьте промпт и сгенерируйте снова.</p>
      <img alt="AI-подсказка" decoding="async" loading="lazy" src={modelAiWrite1} />
      <img alt="Правка промпта и повторная генерация" decoding="async" loading="lazy" src={modelAiWrite2} />

      <h3>3. Примеры</h3>
      <h4>Одежда</h4>
      <figure className="v3-img-pair">
        <img alt="Одежда с моделью, пример 1" decoding="async" loading="lazy" src={modelShowClothes1} />
        <img alt="Одежда с моделью, пример 2" decoding="async" loading="lazy" src={modelShowClothes2} />
      </figure>
      <h4>Кружка</h4>
      <figure className="v3-img-pair">
        <img alt="Кружка с моделью, пример 1" decoding="async" loading="lazy" src={modelShowCup1} />
        <img alt="Кружка с моделью, пример 2" decoding="async" loading="lazy" src={modelShowCup2} />
      </figure>
      <h4>Колье</h4>
      <figure className="v3-img-pair">
        <img alt="Колье с моделью, пример 1" decoding="async" loading="lazy" src={modelShowNecklace1} />
        <img alt="Колье с моделью, пример 2" decoding="async" loading="lazy" src={modelShowNecklace2} />
      </figure>
      <h4>Настольная лампа</h4>
      <figure className="v3-img-pair">
        <img alt="Настольная лампа с моделью, пример 1" decoding="async" loading="lazy" src={modelShowLamp1} />
        <img alt="Настольная лампа с моделью, пример 2" decoding="async" loading="lazy" src={modelShowLamp2} />
      </figure>
      <h4>Секатор</h4>
      <figure className="v3-img-pair">
        <img alt="Секатор с моделью, пример 1" decoding="async" loading="lazy" src={modelShowPruner1} />
        <img alt="Секатор с моделью, пример 2" decoding="async" loading="lazy" src={modelShowPruner2} />
      </figure>
      <h4>Серьги</h4>
      <figure className="v3-img-pair">
        <img alt="Серьги с моделью, пример 1" decoding="async" loading="lazy" src={modelShowEarring1} />
        <img alt="Серьги с моделью, пример 2" decoding="async" loading="lazy" src={modelShowEarring2} />
      </figure>
    </>
  );
};

export default RuContent;
